import { isString } from 'src/utils/type-checks'
import { jexlEval } from 'src/utils/jexl'

export type CommentKeys =
  | 'codes'
  | 'category'
  | 'color'
  | 'unit'
  | 'icon'
  | 'trueValue'
  | 'falseValue'
  | 'hidden'
  | 'asProperty'
  | 'url' // show as link
  | 'textarea'
  | 'qr'
  | 'qiconpicker'
  | 'qcolorpicker'
  | 'twinSelectSingle' // select single twin
  | 'twinSelectMultiple' // select multiple twins
  | 'twinSelectModelIds' // select model IDs for twins
  | 'modelSelect' // select single model
  | 'modelSelectSingle' // select single model
  | 'modelSelectMultiple' // select multiple models
  | 'modelSelectModelIds' // select model IDs to filter the ModelSelect
  | 'propertySelectSingle' // select single property
  | 'propertySelectMultiple' // select multiple properties
  | 'relPropertySelectSingle' // select single rel property
  | 'relPropertySelectMultiple' // select multiple rel properties
  | 'twinImportMappingSelectSingle' // select single twin import mapping
  | 'default'
  | 'hideKeys'
  | 'set'
  | 'fillUserName'
  | 'fillUserOid'
  | 'hasKeyVaultFields' // has fields to generate in TwinEditProperty KeyVault
  | 'keyVaultFields' // fields to generate in TwinEditProperty KeyVault, they ar not stored in the twin
  | 'keyVaultExpression' // expression to save in keyVault in TwinEditProperty KeyVault
  | 'mainFeatureSelectSingle' // Select a specific main feature
  | 'drawerFeatureSelectSingle' // Load a specific drawer feature
  | 'tileSelectSingle' // Select a specific (dashboard) tile
  | 'flowSelectSingle' // Load a specific flow
  | 'activitySelectSingle' // Load a specific flow activity
  | 'relMappings' // show TwinEditProperty RelMappings
  | 'relPropertyMappings' // show TwinEditProperty RelMappings
  | 'relSelect' // show TwinEditProperty RelMapping
  | 'relType' // show TwinEditProperty RelMapping ['incoming','outgoing']
  | 'order' // order of a category or property
  | 'restrictions' // class-specific restrictions for relationships
  | 'jexlExpression' // show jexl functionality popup
  | 'suggestions' // show suggestions in ValueExpressionPopUp (ex. suggestions:FloorGuid )
  | 'sharedQuerySelectSingle' // Use a specific shared query
  | 'fieldIsSourceModelId' // the field contains source modelId
  | 'fieldIsTargetModelId' // the field contains target modelId
  | 'fieldIsRelName' // the field contains relationship name
  | 'accept' // accepted file extensions (ex. accept:'.pdf,.doc,.md')
  | 'fieldIsFlowId' // the field contains flowId
  | 'fieldIsTwinId' // the field contains twinId
  | 'required' // the field is required
  | 'anchor' // anchor position
  | 'self' // anchor position
  | 'code' // Code editor popup (provide language, ex. code:'kql' or code:'json')
  | 'validation' // JEXL validation expression (ex. validation:'closedTime == null || triggerTime == null || isAfterOrEqual(closedTime, triggerTime)')
  | 'validationMessage' // Custom validation error message (ex. validationMessage:'Closed time must be after trigger time')
  | 'secretSelect' // Load a specific secret and store the id
  | 'fabricSelect' // Select a Fabric item (e.g. notebook, report) and store the id
  | 'smsRecipientSelect' // Select users that opted in to SMS notifications and store their phone numbers
  | 'roleSelect' // offer role-name suggestions for a string array (e.g. on a `roles` field)
  | 'fieldType' // specifies the field type (e.g. fieldType:adminFile)

/**
 * Parses comments stored in Digital Twin data models
 * @param data A comment string with the following format "key1:value1;key2:value2;key3:value3"
 * @return An object generated from the comment's data
 */
export const parseComment = (data: string) => {
  if (!data) return undefined

  return data.split(';').reduce<Record<string, string | boolean>>((obj, x) => {
    const key = x.split(':')[0]
    const value = x.replace((key ?? '') + ':', '') // x.split(':')[1]?.includes(',') ? x.split(':')[1].split(',') : x.split(':').slice(1).join(':') // if contains ',' return array, otherwise string
    obj[key] = value === key ? true : value // if no value was defined, key and value would be the same after parsing, we just return true in this case

    return obj
  }, {})
}

/**
 * Get the value of a key in the comment
 * @param data A comment string with the following format "key1:value1;key2:value2;key3:value3"
 * @param key The key to look for
 * @return The value of the specified key (or null if not found)
 */
export const getCommentValue = (data: string | undefined, key: CommentKeys) => {
  if (!data) return undefined
  const dataObject = parseComment(data)
  if (!dataObject) return undefined
  return dataObject[key]
}

/**
 * Get the value of a key in the comment as a string
 * @param data A comment string with the following format "key1:value1;key2:value2;key3:value3"
 * @param key The key to look for
 * @return The value of the specified key as a string (or null if not found)
 */
export const getCommentValueString = (data: string | undefined, key: CommentKeys) => {
  const value = getCommentValue(data, key)
  return isString(value) ? value : undefined
}

/**
 * Evaluate the value of a key in the comment
 * @param data A comment string with the following format "key1:value1;key2:value2;key3:value3"
 * @param key The key to look for
 * @param context Context object (usually the digital twin itself)
 * @return The value of the specified key or the evaluated jexl espression if a string
 */
export const evaluateCommentValue = (
  data: string | undefined,
  key: CommentKeys,
  context?: { [key: string]: unknown }
) => {
  // getCommentValue key hidden (;hidden) without a value will return a boolean
  // getCommentValue key hidden (;hidden:...) with expression will return a string
  const _value = getCommentValue(data, key)
  return isString(_value) ? jexlEval(_value, context) : _value
}

/**
 * Get the default value for a property from its comment
 * @param data A comment string with the following format "key1:value1;key2:value2;key3:value3"
 * @param schema The DTDL schema of the property (for type conversion)
 * @param context Context object (usually the digital twin itself) for evaluating JEXL expressions
 * @return The default value (with appropriate type conversion), or undefined if no default is specified
 */
export const getDefaultValue = (
  data: string | undefined,
  schema: unknown,
  context?: { [key: string]: unknown }
): unknown => {
  const defaultComment = getCommentValue(data, 'default')
  if (!defaultComment) return undefined

  // If it's a JEXL expression (string), evaluate it
  if (isString(defaultComment)) {
    const evaluated = jexlEval(defaultComment, context)

    // If JEXL returns undefined, treat the string as a literal value
    if (evaluated === undefined) {
      return defaultComment
    }

    // Type conversion based on schema
    if (schema === 'integer' || schema === 'long') {
      const parsed = Number.parseInt(evaluated as string, 10)
      return Number.isNaN(parsed) ? evaluated : parsed
    }
    if (schema === 'double' || schema === 'float') {
      const parsed = Number.parseFloat(evaluated as string)
      return Number.isNaN(parsed) ? evaluated : parsed
    }
    if (schema === 'boolean') {
      if (typeof evaluated === 'boolean') return evaluated
      if (typeof evaluated === 'string') {
        const lower = evaluated?.toLowerCase()
        return lower === 'true' || lower === '1'
      }
      return Boolean(evaluated)
    }
    return evaluated
  }

  // If it's already a boolean or other type, return as-is
  return defaultComment
}
